import { Box, Button, Collapse, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Vector from "../../../../../../../assets/images/svgs/VectorIcon.svg";
import AlertTriangle from "../../../../../../../assets/images/svgs/alert-triangle.svg";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";
import styles from "./PromptTextToImageError.module.scss";

type Props = {
	className?: string;
	showDismissButton?: boolean;
	icon?: React.ReactNode;
	containerClassName?: string;
	warningContainerClassName?: string;
};

const PromptTextToImageError = ({
	className,
	showDismissButton = true,
	icon,
	containerClassName,
	warningContainerClassName,
}: Props) => {
	const { t, i18n } = useTranslation("translation", { keyPrefix: "playground.textToImage.errors" });
	const { textToImageStore } = useAppStore();
	const [open, setOpened] = useState(false);
	const [message, setMessage] = useState("");
	const [boldMessage, setBoldMessage] = useState("");
	const [errorStyle, setErrorStyle] = useState<"error" | "warning">("error");

	useEffect(() => {
		const isErrorMessageExists = i18n.exists(
			`playground.textToImage.errors.${textToImageStore.errors?.generate?.message}`,
		);
		if (isErrorMessageExists) {
			// Check if this is a warning or an error
			const isWarning = textToImageStore.errors?.generate?.isWarning;
			setErrorStyle(isWarning ? "warning" : "error");
			setOpened(true);
			setMessage(t(`${textToImageStore.errors?.generate?.message}`));
		} else {
			setOpened(false);
			setBoldMessage("");
			setMessage("");
		}
	}, [textToImageStore.errors.generate]);

	return (
		<Collapse in={open} onExited={() => (textToImageStore.errors = {})} className={containerClassName}>
			{errorStyle == "error" ? (
				<Box className={`${styles.errorMessageContainer} ${className || ""}`}>
					{icon || <img src={Vector} />}
					<Typography className={styles.errorMessage}>{message}</Typography>
				</Box>
			) : (
				<Box className={clsx(styles.warningMessageContainer, warningContainerClassName)}>
					{
						<>
							<Box className={styles.warningTextContainer}>
								{icon || <img src={AlertTriangle} />}
								<Typography className={styles.warningMessage}>
									<span className={styles.warningBoldMessage}>{boldMessage}</span> {message}
								</Typography>
							</Box>
							{showDismissButton && (
								<Button
									className={styles.warningDismissButton}
									onClick={() => (textToImageStore.errors = {})}
								>
									{t("dismissButton")}
								</Button>
							)}
						</>
					}
				</Box>
			)}
		</Collapse>
	);
};

export default observer(PromptTextToImageError);
